import clsx from 'clsx';
import Image from 'next/image';
import { useEffect } from 'react';
import {
  type SegmentAttributes,
  useSegmentInteraction,
} from '../../../../lib/analytics/useSegmentInteraction';
import { type PromoSpot } from '../../../../services/globalNavItems/utils';
import { Button } from '../../../Button';
import { Typography } from '../../../Typography';

type PromoSpotProps = {
  promoSpot: PromoSpot;
  open: boolean;
};

export function PromoSpot({ promoSpot, open }: PromoSpotProps) {
  const segmentAttributes: SegmentAttributes = {
    entity_name: 'global_nav_promo_cta',
  };

  const firePromoSpotVisibleEvent = useSegmentInteraction({
    eventName: 'link_viewed',
    attributes: segmentAttributes,
  });

  const fireButtonClickedEvent = useSegmentInteraction({
    eventName: 'link_clicked',
    attributes: segmentAttributes,
  });

  useEffect(() => {
    if (open) {
      firePromoSpotVisibleEvent({
        link_copy: promoSpot.cta.text,
        link_position: 0,
        destination_url: promoSpot.cta.url,
      });
    }
  }, [open, promoSpot.cta.text, promoSpot.cta.url, firePromoSpotVisibleEvent]);

  return (
    <div
      className={clsx(
        'bg-[#effcf8]',
        'max-w-[270px]',
        'mt-[-5px]',
        'flex',
        'flex-col',
        'gap-2',
        'mb-8',
      )}
    >
      <Image
        src={promoSpot.image.src}
        alt={promoSpot.image.alt ?? 'Promo spot image'}
        width={promoSpot.image.width}
        height={promoSpot.image.height}
        className={clsx('max-w-full')}
        loading="lazy"
      />
      <div className={clsx('py-6', 'px-4', 'flex', 'flex-col', 'gap-2')}>
        <Typography
          variant="text-medium-bold"
          className={clsx('!font-medium', 'text-center', 'text-neutral-darkest')}
        >
          {promoSpot.title.text}
        </Typography>
        <Typography variant="text-xsmall" className={clsx('text-center', 'text-neutral-darker')}>
          {promoSpot.body}
        </Typography>
        <div className={clsx('mt-2', 'flex', 'flex-col', 'items-center', 'gap-2')}>
          <Button
            variant="primary"
            component="a"
            onClick={() => fireButtonClickedEvent()}
            href={`${promoSpot.cta.url}`}
            aria-label={promoSpot.cta.text}
          >
            {promoSpot.cta.text}
          </Button>
          <Typography variant="text-xsmall" className={clsx('text-center', 'text-neutral-darker')}>
            {promoSpot.cta.subtext}
          </Typography>
        </div>
      </div>
    </div>
  );
}
